/**
 * FarmLocationEditor component for editing farm location (region, city, vereda).
 * Extracted from FarmDetailsModal.tsx.
 */

import React from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

interface FarmLocationEditorProps {
  isEditing: boolean;
  regionName?: string;
  cityName?: string;
  veredaName?: string;
  selectedRegionId: string;
  selectedCityId: string;
  selectedVeredaId: string;
  regions: any[];
  cities: any[];
  veredas: any[];
  isUpdatingLocation: boolean;
  onRegionChange: (value: string) => void;
  onCityChange: (value: string) => void;
  onVeredaChange: (value: string) => void;
  onCreateRegion: () => void;
  onCreateCity: () => void;
  onCreateVereda: () => void;
  onSave: () => void;
  onCancel: () => void;
}

export const FarmLocationEditor: React.FC<FarmLocationEditorProps> = React.memo(({
  isEditing,
  regionName,
  cityName,
  veredaName,
  selectedRegionId,
  selectedCityId,
  selectedVeredaId,
  regions,
  cities,
  veredas,

  onRegionChange,
  onCityChange,
  onVeredaChange,
  onCreateRegion,
  onCreateCity,
  onCreateVereda,


}) => {
  if (!veredaName) {
    return null;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={6}>
        {isEditing ? (
          <Box>
            <Box display="flex" gap={1} alignItems="flex-start">
              <Box flex={1}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Departamento</InputLabel>
                  <Select
                    value={selectedRegionId}
                    onChange={(e) => {
                      onRegionChange(e.target.value);
                      onCityChange(""); // Reset city when region changes
                      onVeredaChange(""); // Reset vereda when region changes
                    }}
                    label="Departamento"
                  >
                    {regions.map((region) => (
                      <MenuItem key={region.id} value={region.id}>
                        {region.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={onCreateRegion}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  height: "56px",
                  borderColor: "var(--primary)",
                  color: "var(--primary)",
                  "&:hover": {
                    backgroundColor: "var(--tertiary)",
                    borderColor: "var(--primary)",
                  },
                }}
                title="Crear nuevo departamento"
              ></Button>
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 1, display: "block" }}
            >
              Seleccione el departamento donde se ubica la finca
            </Typography>
          </Box>
        ) : (
          <TextField
            label="Departamento"
            value={regionName || "N/A"}
            fullWidth
            disabled
            variant="outlined"
          />
        )}
      </Grid>
      <Grid item xs={6}>
        {isEditing ? (
          <Box>
            <Box display="flex" gap={1} alignItems="flex-start">
              <Box flex={1}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Municipio</InputLabel>
                  <Select
                    value={selectedCityId}
                    onChange={(e) => {
                      onCityChange(e.target.value);
                      onVeredaChange(""); // Reset vereda when city changes
                    }}
                    label="Municipio"
                    disabled={!selectedRegionId}
                  >
                    {!selectedRegionId && (
                      <MenuItem disabled>
                        Seleccione primero un departamento
                      </MenuItem>
                    )}
                    {cities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={onCreateCity}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  height: "56px",
                  borderColor: "var(--primary)",
                  color: "var(--primary)",
                  "&:hover": {
                    backgroundColor: "var(--tertiary)",
                    borderColor: "var(--primary)",
                  },
                }}
                title="Crear nuevo municipio"
              ></Button>
            </Box>
            <Typography
              variant="caption"
              color={!selectedRegionId ? "error" : "textSecondary"}
              sx={{ mt: 1, display: "block" }}
            >
              {!selectedRegionId
                ? "Primero debe seleccionar un departamento"
                : "Seleccione el municipio correspondiente al departamento"}
            </Typography>
          </Box>
        ) : (
          <TextField
            label="Municipio"
            value={cityName || "N/A"}
            fullWidth
            disabled
            variant="outlined"
          />
        )}
      </Grid>
      <Grid item xs={12}>
        {isEditing ? (
          <Box>
            <Box
              display="flex"
              gap={1}
              alignItems="flex-start"
            >
              <Box flex={1}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Vereda</InputLabel>
                  <Select
                    value={selectedVeredaId}
                    onChange={(e) => onVeredaChange(e.target.value)}
                    label="Vereda"
                    disabled={!selectedRegionId || !selectedCityId}
                  >
                    {(!selectedRegionId ||
                      !selectedCityId) && (
                      <MenuItem disabled>
                        Seleccione primero departamento y
                        municipio
                      </MenuItem>
                    )}
                    {veredas.map((vereda) => (
                      <MenuItem
                        key={vereda.id}
                        value={vereda.id}
                      >
                        {vereda.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={onCreateVereda}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  height: "56px",
                  borderColor: "var(--primary)",
                  color: "var(--primary)",
                  "&:hover": {
                    backgroundColor: "var(--tertiary)",
                    borderColor: "var(--primary)",
                  },
                }}
                title="Crear nueva vereda"
              ></Button>
            </Box>
            <Typography
              variant="caption"
              color={
                !selectedRegionId || !selectedCityId
                  ? "error"
                  : "textSecondary"
              }
              sx={{ mt: 1, display: "block" }}
            >
              {!selectedRegionId
                ? "Primero debe seleccionar un departamento y municipio"
                : !selectedCityId
                  ? "Primero debe seleccionar un municipio"
                  : "Seleccione la vereda donde se ubica la finca"}
            </Typography>
          </Box>
        ) : (
          <TextField
            label="Vereda"
            value={veredaName}
            fullWidth
            disabled
            variant="outlined"
          />
        )}
      </Grid>
    </Grid>
  );
});
FarmLocationEditor.displayName = "FarmLocationEditor";
